import { Link } from "react-router-dom";

import Footer from "../components/Footer";
import SiteNav from "../components/SiteNav";
import { useBusiness } from "../data/BusinessContext";
import "./LegalPage.css";

/**
 * Impressum and Datenschutzerklärung. Deliberately German only: the legal
 * texts are for a German-speaking audience and were specified by the
 * operator; the rest of the site stays multilingual around them.
 */

// Punycode in the href so every mail client resolves the umlaut domain.
const MAIL = { shown: "info@dähääm.de", href: "mailto:info@xn--dhm-qlaba.de" };
const UPDATED = "24. Oktober 2026";

function Operator({ phone }: { phone: string }) {
  return (
    <>
      <p>
        <strong>emma24 Alexander Wimmer</strong>
        <br />
        Inhaber: Alexander Wimmer
        <br />
        Hauptstraße 133
        <br />
        69117 Heidelberg
        <br />
        Deutschland
      </p>
      <p>
        <strong>Betriebsstätte Dähääm</strong>
        <br />
        Hauptstraße 129
        <br />
        69117 Heidelberg
        <br />
        Deutschland
      </p>
      <h2>Kontakt</h2>
      <p>
        {phone && (
          <>
            Telefon: <a className="link" href={`tel:${phone.replace(/[\s/()-]/g, "")}`}>{phone}</a>
            <br />
          </>
        )}
        E-Mail: <a className="link" href={MAIL.href}>{MAIL.shown}</a>
      </p>
    </>
  );
}

export function ImpressumPage() {
  const business = useBusiness();
  return (
    <div className="legal-page">
      <SiteNav />
      <main className="site-inner legal" id="main" lang="de">
        <p className="eyebrow">Rechtliches</p>
        <h1>Impressum</h1>
        <p className="legal-lead">Angaben gemäß § 5 Digitale-Dienste-Gesetz (DDG)</p>

        <Operator phone={business.phone} />

        <h2>Umsatzsteuer-Identifikationsnummer</h2>
        <p>
          Umsatzsteuer-Identifikationsnummer gemäß § 27a Umsatzsteuergesetz:
          <br />
          DE168301044
        </p>

        <h2>Verbraucherstreitbeilegung</h2>
        <p>Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</p>

        <p className="legal-foot">
          <Link className="link" to="/datenschutz">Datenschutzerklärung</Link>
        </p>
      </main>
      <Footer business={business} />
    </div>
  );
}

export function DatenschutzPage() {
  const business = useBusiness();
  return (
    <div className="legal-page">
      <SiteNav />
      <main className="site-inner legal" id="main" lang="de">
        <p className="eyebrow">Rechtliches</p>
        <h1>Datenschutzerklärung</h1>
        <p className="legal-lead">Stand: {UPDATED}</p>

        <h2>1. Verantwortlicher</h2>
        <Operator phone={business.phone} />

        <h2>2. Hosting und Server-Logfiles</h2>
        <p>
          Diese Website wird bei der Railway Corporation, 548 Market St PMB 68956, San Francisco, CA 94104, USA, gehostet. Beim
          Aufruf der Website verarbeitet der Hosting-Anbieter automatisch Informationen in Server-Logfiles: IP-Adresse, Datum und
          Uhrzeit des Zugriffs, aufgerufene Seite, HTTP-Statuscode, übertragene Datenmenge sowie Browsertyp und -version
          (User-Agent). Diese Daten dienen dem technischen Betrieb, der Stabilität und der Sicherheit der Website und werden nicht
          mit anderen Daten zusammengeführt. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Die Logfiles werden nach 7 Tagen
          gelöscht.
        </p>
        <p>
          Die Verarbeitung findet in den USA statt. Railway stellt ein Data Processing Addendum bereit und stützt die Übermittlung
          auf die EU-Standardvertragsklauseln sowie das EU-US Data Privacy Framework.
        </p>

        <h2>3. Bilder (Content Delivery Network)</h2>
        <p>
          Die Fotos dieser Website werden über das Content Delivery Network von Cloudinary Ltd. ausgeliefert. Beim Laden eines
          Bildes übermittelt Ihr Browser Ihre IP-Adresse an Cloudinary. Zweck ist die schnelle, geräteangepasste Auslieferung der
          Bilder; Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Cloudinary ist unter dem EU-US Data Privacy Framework
          zertifiziert.
        </p>

        <h2>4. Schriften</h2>
        <p>Die verwendeten Schriften sind lokal auf unserem Server gespeichert. Beim Aufruf der Website wird keine Verbindung zu Servern von Google oder anderen Schriftanbietern aufgebaut.</p>

        <h2>5. Reservierungsformular</h2>
        <p>
          Über das Reservierungsformular können Sie einen Tisch anfragen. Pflichtangaben sind Datum, Uhrzeit, Personenzahl, Name
          und Telefonnummer. Freiwillig sind E-Mail-Adresse, Anlass, Sitzplatzwunsch (drinnen/draußen) und Wünsche. Wir
          verarbeiten diese Angaben ausschließlich zur Bearbeitung Ihrer Reservierung, für Rückfragen und zur Bestätigung oder
          Absage. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung).
        </p>
        <p>
          Die Angaben werden in unserer Reservierungsdatenbank beim Hosting-Anbieter Railway (siehe Ziffer 2) gespeichert und
          zusätzlich per E-Mail an unser Reservierungspostfach zugestellt. Das Postfach wird von der Host Europe GmbH,
          Hansestraße 111, 51149 Köln, betrieben. Wenn Sie eine E-Mail-Adresse angeben, erhalten Sie eine automatische
          Eingangsbestätigung sowie die Bestätigung oder Absage Ihrer Anfrage per E-Mail.
        </p>
        <p>
          Reservierungsdaten werden 30 Tage nach dem Reservierungstermin automatisch gelöscht. Eine Weitergabe an Dritte findet
          nicht statt, abgesehen von den genannten technischen Dienstleistern.
        </p>

        <h2>6. Kontakt per E-Mail oder Telefon</h2>
        <p>
          Wenn Sie uns per E-Mail oder Telefon kontaktieren, verarbeiten wir Ihre Angaben zur Bearbeitung Ihrer Anfrage
          (Art. 6 Abs. 1 lit. b bzw. lit. f DSGVO). Unsere E-Mail-Postfächer werden von der Host Europe GmbH, Hansestraße 111,
          51149 Köln, betrieben. Die Daten werden gelöscht, sobald Ihre Anfrage erledigt ist und keine gesetzlichen
          Aufbewahrungspflichten entgegenstehen.
        </p>

        <h2>7. Cookies und lokale Speicherung</h2>
        <p>
          Diese Website setzt keine Cookies. Ihr Browser speichert lediglich technisch notwendige Einstellungen lokal auf Ihrem
          Gerät: die gewählte Sprache, das gewählte Farbschema (hell/dunkel) und den Hinweis, dass die Startanimation in dieser
          Sitzung bereits gezeigt wurde. Diese Angaben werden nicht an uns oder Dritte übermittelt und dienen keiner Analyse.
          Sie sind für die von Ihnen gewünschte Darstellung erforderlich (§ 25 Abs. 2 Nr. 2 TDDDG); eine Einwilligung ist dafür
          nicht nötig. Sie können die Einträge jederzeit über die Einstellungen Ihres Browsers löschen.
        </p>

        <h2>8. Keine Analyse- und Marketing-Dienste</h2>
        <p>
          Wir setzen keine Analyse-, Tracking- oder Marketing-Dienste ein, insbesondere kein Google Analytics, keinen Google Tag
          Manager, kein Meta-Pixel und kein reCAPTCHA. Instagram und Google Maps sind lediglich als externe Links eingebunden;
          beim Aufruf dieser Links gelten die Datenschutzbestimmungen des jeweiligen Anbieters. Es werden keine Inhalte dieser
          Dienste auf unserer Website geladen.
        </p>

        <h2>9. Empfänger und Drittlandübermittlung</h2>
        <p>
          Empfänger personenbezogener Daten sind ausschließlich die genannten Dienstleister: Railway Corporation (Hosting und
          Datenbank, USA), Cloudinary Ltd. (Bildauslieferung, USA/Israel) und Host Europe GmbH (E-Mail, Deutschland). Für die
          Übermittlung in die USA bestehen die unter Ziffer 2 und 3 genannten Garantien.
        </p>

        <h2>10. Ihre Rechte</h2>
        <p>
          Sie haben das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung der
          Verarbeitung (Art. 18) und Datenübertragbarkeit (Art. 20). Einer Verarbeitung auf Grundlage von Art. 6 Abs. 1 lit. f
          DSGVO können Sie jederzeit widersprechen (Art. 21 DSGVO). Eine erteilte Einwilligung können Sie jederzeit mit Wirkung
          für die Zukunft widerrufen. Wenden Sie sich dazu an die oben genannten Kontaktdaten.
        </p>
        <p>
          Sie haben außerdem das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren. Zuständig ist der
          Landesbeauftragte für den Datenschutz und die Informationsfreiheit Baden-Württemberg, Lautenschlagerstraße 20,
          70173 Stuttgart.
        </p>

        <h2>11. Verschlüsselung</h2>
        <p>Die Übertragung aller Daten zwischen Ihrem Browser und dieser Website erfolgt TLS-verschlüsselt (HTTPS).</p>

        <p className="legal-foot">
          <Link className="link" to="/impressum">Impressum</Link>
        </p>
      </main>
      <Footer business={business} />
    </div>
  );
}
